import { getSiteOrigin } from "@/lib/siteUrl";
import { LAUNCHLAB_ESTIMATED_CREATE_FEE_SOL } from "@/config/idxLaunchlabAppConfig";
import { IDX_LAUNCHLAB_ON_CHAIN_LP_SPLIT } from "@/lib/idx-launchlab/platform-on-chain-config";

const siteOrigin = getSiteOrigin();
const PATH = "/idx-launchlab";
const pageUrl = `${siteOrigin}${PATH}`;

/** Default quote mint for v1 launches — WSOL */
export const IDX_LAUNCHLAB_QUOTE_MINT_SOL =
  "So11111111111111111111111111111111111111112";

/** LP split scales for CreatePlatformConfig (sum = 1_000_000) — mirrors DEV-REFERENCE.md */
const IDX_LAUNCHLAB_LP_SPLIT = { ...IDX_LAUNCHLAB_ON_CHAIN_LP_SPLIT };

/** Canonical IDX Launchlab fee constants — mirrors DEV-REFERENCE.md / platform-on-chain-config.js */
export const IDX_LAUNCHLAB_FEES = {
  /** Basis points (1 bps = 0.01%) */
  platformBps: 50,
  creatorBps: 50,
  /** On-chain PlatformConfig fields (per 1_000_000) */
  platformRatePerMillion: 5000,
  creatorRatePerMillion: 5000,
  /** Approximate all-in trader fee on bonding-curve trades (doc/build parity; not on landing page) */
  totalAllInPercent: 2,
  creatorTagline: "On bonding curve trades",
  lpSplit: IDX_LAUNCHLAB_LP_SPLIT,
};

/** Platform PDA + v1 launch UX settings — mirrors DEV-REFERENCE.md / platform-on-chain-config.js */
export const IDX_LAUNCHLAB_PLATFORM_SETTINGS = {
  curveType: 0,
  supplyHuman: 1_000_000_000,
  decimals: 6,
  graduationFractionBps: 8000,
  quoteMint: IDX_LAUNCHLAB_QUOTE_MINT_SOL,
  quoteSymbol: "SOL",
  vestingEnabled: false,
  launchUxVersion: "v1-simple",
  lpSplit: IDX_LAUNCHLAB_LP_SPLIT,
  fees: {
    platformRatePerMillion: IDX_LAUNCHLAB_FEES.platformRatePerMillion,
    creatorRatePerMillion: IDX_LAUNCHLAB_FEES.creatorRatePerMillion,
  },
  mutableVia: "UpdatePlatformConfig",
};

function formatPercentFromBps(bps) {
  return `${bps / 100}%`;
}

function formatLpCreatorFeeShareLabel(lpSplit) {
  const pct = (lpSplit.creatorScale / 1_000_000) * 100;
  const formatted = Number.isInteger(pct) ? String(pct) : pct.toFixed(1);
  return `~${formatted}%`;
}

const creatorRateLabel = formatPercentFromBps(IDX_LAUNCHLAB_FEES.creatorBps);
const creatorLpFeeShareLabel = formatLpCreatorFeeShareLabel(
  IDX_LAUNCHLAB_LP_SPLIT,
);

export const idxLaunchlabLandingConfig = {
  canonicalPath: PATH,

  seo: {
    title: "IDX Launch Lab | Create Crypto Tokens & Solana Meme Coins Easily",
    description:
      "Create crypto tokens in minutes with IDX Launch Lab, an easy-to-use token maker and meme coin generator. Launch custom blockchain tokens quickly with a secure, streamlined process.",
    keywords: [
      "create crypto coin solana",
      "IDX Launchlab",
      "coin launchpad solana",
      "create your own cryptocurrency",
      "bonding curve coin launch",
      "business coin launch crypto",
      "fair launch solana coin",
      "no code coin creator",
      "idx launchlab bonding curve",
      "solana meme coin creator",
      "boost solana coin after launch",
      "idx volume bot launchlab",
      "solana holders maker",
      "coin reaction booster",
    ],
    ogTitle: "IDX Launch Lab | Create Crypto Tokens & Solana Meme Coins Easily",
    ogDescription:
      "Create crypto tokens in minutes with IDX Launch Lab, an easy-to-use token maker and meme coin generator. Launch custom blockchain tokens quickly with a secure, streamlined process.",
    ogImage: `${siteOrigin}/og-idx-launchlab.png`,
    ogImageWidth: 1024,
    ogImageHeight: 682,
    ogAlt: "IDX Launchlab — Create your crypto coin on Solana",
  },

  schema: {
    appName: "IDX Launchlab — Crypto Coin Creator",
    appDescription:
      "Create crypto tokens in minutes with IDX Launch Lab — an easy-to-use token maker and meme coin generator on Solana. Fair-launch bonding curve, no code, auto-graduation to a liquidity pool.",
    howToName: "How to create your crypto coin with IDX Launchlab",
    howToSteps: [
      {
        name: "Connect your Solana wallet",
        text: "Connect IDX Wallet or any Solana wallet (Phantom, Backpack, etc.) to get started. No sign-up needed.",
      },
      {
        name: "Name your coin",
        text: "Enter your coin name, ticker symbol, description, and upload a logo. Make it memorable.",
      },
      {
        name: "Launch your coin",
        text: "Deploy your coin to the IDX Launchlab bonding curve with a single transaction.",
      },
      {
        name: "Grow and graduate",
        text: "As trading volume fills the curve, your coin auto-graduates to a full Solana liquidity pool.",
      },
      {
        name: "Boost after launch",
        text: "Use IDX Volume Bot, Holders Maker, and Reaction Booster to grow trading activity, holder count, and community engagement on your live mint.",
      },
    ],
  },

  hero: {
    eyebrow: "IDX Launchlab Coin Creator",
    h1Primary: "Create Your Crypto Coin",
    h1Secondary: "Liquid your idea — launch on Solana's bonding curve",
    heroParagraph:
      "Turn any idea, brand, or business into a crypto coin in minutes. IDX Launchlab's bonding curve offers fair launch, no presale, no code required. Your coin goes live on Solana instantly and auto-graduates to a liquidity pool when the curve fills.",
    mainAriaLabel: "IDX Launchlab coin creator page",
    titleId: "idx-launchlab-page-title",
    background: {
      src: "/idx-launchlab-hero-bg.png",
      alt: "IDX Launchlab — create your crypto coin on Solana",
      width: 1024,
      height: 576,
      aspectRatio: "16 / 9",
    },
  },

  cta: {
    primaryLabel: "Start Now",
    primaryHref: "/idx-launchlab/live",
    createSectionId: "create",
  },

  creatorFee: {
    heroChip: {
      label: "Creator",
      rate: creatorRateLabel,
      tagline: IDX_LAUNCHLAB_FEES.creatorTagline,
    },
    callout: {
      eyebrow: "Creator earnings",
      title: `Earn ${creatorRateLabel} on curve trades, ${creatorLpFeeShareLabel} of pool fees after graduation`,
      description: `While your coin trades on the IDX Launchlab bonding curve, you earn ${creatorRateLabel} on every buy and sell. Fees accrue in your creator vault — claim anytime from Rewards.`,
      detail: `After graduation, the curve fee stops. You receive a Fee Key NFT and earn ${creatorLpFeeShareLabel} of ongoing pool trading fees — also claimable from Rewards.`,
    },
  },

  howItWorks: {
    headingBefore: "How ",
    headingHighlight: "IDX Launchlab",
    headingAfter: " Works",
    subtitle:
      "From wallet connect to live coin — the fastest fair-launch path on Solana.",
    steps: [
      {
        step: "01",
        title: "Connect Your Wallet",
        detail:
          "Connect IDX Wallet or any Solana wallet (Phantom, Backpack, Solflare). No account or sign-up required.",
      },
      {
        step: "02",
        title: "Name Your Coin",
        detail:
          "Set a name, ticker symbol, description, and socials. Upload a logo to make your coin stand out.",
      },
      {
        step: "03",
        title: "Launch Your Coin",
        detail:
          "Deploy to the bonding curve with one transaction. Your coin is live on Solana immediately.",
      },
      {
        step: "04",
        title: "Grow & Graduate",
        detail:
          "Community trading fills the bonding curve. When complete, your coin auto-graduates to a Solana AMM liquidity pool.",
      },
    ],
  },

  explanation: {
    title: "What IDX Launchlab does",
    paragraphs: [
      "IDX Launchlab is a no-code coin creation and launch tool on Solana. Instead of hiring developers or deploying custom contracts, you can create, configure, and launch a Token-2022 coin through our interface in a few clicks.",
      "Every coin launched through IDX Launchlab uses a bonding curve — a transparent, on-chain price curve that starts at a low price and rises as more people buy in. There is no presale, no VC allocation, and no hidden wallet — just a fair open market from the first trade.",
      "Once trading volume fills the curve, IDX Launchlab automatically graduates your coin to a real Solana liquidity pool, making it tradeable on every Solana DEX aggregator including Jupiter, Birdeye, and DexScreener.",
    ],
    relatedLinks: [
      {
        href: "/idx-volume-bot",
        label: "boost trading volume with the IDX volume bot",
      },
      { href: "/holders-maker", label: "grow holder count with Holders Maker" },
      {
        href: "/reaction-booster",
        label: "drive social reactions with Reaction Booster",
      },
    ],
  },

  faq: {
    title: "IDX Launchlab FAQ",
    items: [
      {
        question: "What is IDX Launchlab?",
        answer:
          "IDX Launchlab is IDX's coin launchpad built on a bonding curve. Coins start at a low entry price and rise as trading volume grows. When the curve completes, the coin automatically migrates to a Solana AMM liquidity pool — no manual setup required.",
      },
      {
        question: "Do I need to write code to launch a coin?",
        answer:
          "No. IDX Launchlab is a fully no-code interface. You fill in your coin details (name, symbol, logo, description), connect your wallet, and we handle the fair-launch bonding curve and on-chain deployment.",
      },
      {
        question: "Is there a presale or VC allocation?",
        answer:
          "No. IDX Launchlab uses a bonding curve model — every coin starts with the same conditions for all buyers. There is no private sale or preferential allocation. For that, use Create Token Pro.",
      },
      {
        question: "How much does it cost to launch a coin?",
        answer: `Launch fees and on-chain rent are typically about ${LAUNCHLAB_ESTIMATED_CREATE_FEE_SOL} SOL for create-only (your wallet may show a slightly higher line item). Add any optional first buy on top. The exact amount is shown in your wallet before you confirm.`,
      },
      {
        question: "Do coin creators earn fees from trading?",
        answer: `Yes. On the bonding curve, creators earn a ${creatorRateLabel} fee on every buy and sell — claimable from your creator vault. After graduation, the curve fee stops; your Fee Key NFT earns ${creatorLpFeeShareLabel} of pool trading fees on your locked liquidity — also claimable.`,
      },
      {
        question: "What happens when the bonding curve fills?",
        answer:
          "Once the target volume is reached, IDX Launchlab automatically moves your coin and the accumulated SOL into a permanent Solana AMM liquidity pool. Your coin becomes tradeable on all Solana DEXs instantly.",
      },
      {
        question: "Can I launch a business or brand coin?",
        answer:
          "Yes. IDX Launchlab works for meme coins, community coins, brand coins, and business launch projects. Any concept with a name, symbol, and a story can launch here.",
      },
      {
        question: "How can I boost my coin after launch?",
        answer:
          "Once your coin is live, use the IDX Volume Bot (/idx-volume-bot) for automated trading and chart visibility — curve trades also earn creator fees. Holders Maker (/holders-maker) grows visible holder count. Reaction Booster (/reaction-booster) runs engagement campaigns for social momentum. Click Boost coin in the Launchlab toolbar to open the volume bot.",
      },
      {
        question: "Can I run a volume bot after launch?",
        answer:
          "Yes. The IDX Volume Bot is built for Launchlab mints on the bonding curve and after graduation. It increases trading activity and can improve visibility on DexScreener and other discovery platforms. See /volume-bot/learn-more for strategy details.",
      },
    ],
  },
};

export function buildIdxLaunchlabMetadata() {
  const config = idxLaunchlabLandingConfig;
  return {
    title: { absolute: config.seo.title },
    description: config.seo.description,
    keywords: config.seo.keywords,
    alternates: { canonical: pageUrl },
    openGraph: {
      title: config.seo.ogTitle,
      description: config.seo.ogDescription,
      url: pageUrl,
      siteName: "IDX Solana",
      images: [
        {
          url: config.seo.ogImage,
          width: config.seo.ogImageWidth,
          height: config.seo.ogImageHeight,
          alt: config.seo.ogAlt,
        },
      ],
      locale: "en_US",
      type: "website",
    },
    twitter: {
      card: "summary_large_image",
      title: config.seo.ogTitle,
      description: config.seo.ogDescription,
      images: [config.seo.ogImage],
    },
    robots: { index: true, follow: true },
  };
}

export function buildIdxLaunchlabJsonLd() {
  const config = idxLaunchlabLandingConfig;
  const graph = [
    {
      "@type": "SoftwareApplication",
      name: config.schema.appName,
      description: config.schema.appDescription,
      url: pageUrl,
      applicationCategory: "FinanceApplication",
      operatingSystem: "Web",
    },
    {
      "@type": "HowTo",
      name: config.schema.howToName,
      step: config.schema.howToSteps.map((s, i) => ({
        "@type": "HowToStep",
        position: i + 1,
        name: s.name,
        text: s.text,
      })),
    },
    {
      "@type": "FAQPage",
      mainEntity: config.faq.items.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answer,
        },
      })),
    },
  ];

  return { "@context": "https://schema.org", "@graph": graph };
}
